<script setup>
import { computed, inject, ref } from "vue";
import { usePage } from "@inertiajs/vue3";
const route = inject("route");
import { get, forIn } from "lodash-es";
import { useReCaptcha } from "vue-recaptcha-v3";
import Button from "@/Components/ui/button/Button.vue";
import ButtonArrow from "@/Components/svg/ButtonArrow.vue";
import { useForm } from "@inertiajs/vue3";
import FieldContainer from "@/Components/forms/FieldContainer.vue";
import SelectContainer from "@/Components/forms/SelectContainer.vue";
import CheckboxContainer from "@/Components/forms/CheckboxContainer.vue";
import FeedbackMessage from "@/Components/forms/FeedbackMessage.vue";
import Loading from "@/Components/svg/Loading.vue";

const props = defineProps({
  form: {
    type: Object,
    required: true,
  },
});

const {
  props: { path, ziggy },
} = usePage();

const formSchema = computed(() => {
  const result = {
    page_path: path,
  };

  if (!props?.form?.fields) return result;

  forIn(props.form.fields, (value) => {
    const fieldType = get(value, "data.type");
    if (fieldType === "checkbox") {
      const options = get(value, "data.options", []);
      result[get(value, "data.field_name")] = options.length > 1 ? [] : "";
      return;
    }
    result[get(value, "data.field_name")] = "";
  });
  return result;
});

const formObject = useForm(formSchema.value);

const formHasErrors = computed(() => {
  return Object.keys(formObject.errors).length > 0;
});

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const recaptcha = async (event) => {
  event.preventDefault();
  await recaptchaLoaded();
  formObject.captcha_token = await executeRecaptcha("form");
  submitForm();
};

const resetForm = () => {
  formObject.reset();
  formObject.clearErrors();
};

const success = ref(false);

const submitForm = () => {
  formObject.post(route("form.submit", { formId: props.form.id }), {
    preserveScroll: true,
    preserveState: true,
    _method: "get",
    onSuccess: (data) => {
      success.value = true;
      setTimeout(() => {
        resetForm();
        success.value = false;
      }, 5000);
    },
    onError: (error) => {
      console.log("error", error);
    },
  });
};
</script>

<template>
  <div class="form-items w-full">
    <form
      action="#"
      class="grid gap-x-3 grid-cols-2 w-full"
      v-if="props?.form?.fields"
      autocomplete="off"
      @submit.prevent="recaptcha"
    >
      <template
        v-for="(
          { data: { type, field_name, label, width, error_message, options } },
          index
        ) in props.form.fields"
        :key="index"
      >
        <FieldContainer
          v-if="type === 'text' || type === 'textarea'"
          :label="label"
          class=""
          :value="formObject[field_name]"
          :class="width"
          :type="type"
          :error-message="error_message"
          :errors="formObject.errors"
          :field="field_name"
        >
          <template #input="{ setRef }">
            <input
              v-if="type === 'text'"
              :id="field_name"
              v-model="formObject[field_name]"
              type="text"
              :ref="(el) => setRef(el)"
              class="form-input"
              autocomplete="off"
            />
            <textarea
              v-if="type === 'textarea'"
              :id="field_name"
              v-model="formObject[field_name]"
              :ref="(el) => setRef(el)"
              class="form-textarea"
              autocomplete="off"
            />
          </template>
        </FieldContainer>
        <SelectContainer
          v-if="type === 'select'"
          :label="label"
          class="mt-auto w-full"
          :class="width"
          :value="formObject[field_name]"
          :error-message="error_message"
          :errors="formObject.errors"
          :field="field_name"
        >
          <template #select="{ setRef }">
            <select
              class="form-select"
              :id="field_name"
              v-model="formObject[field_name]"
              :ref="(el) => setRef(el)"
              autocomplete="off"
            >
              <option
                v-for="(option, index) in options"
                :key="index"
                :value="option.value"
              >
                {{ option.value }}
              </option>
            </select>
          </template>
        </SelectContainer>
        <CheckboxContainer
          v-if="type === 'checkbox'"
          :label="label"
          :class="width"
          :error-message="error_message"
          :errors="formObject.errors"
          :field="field_name"
        >
          <label
            v-for="({ value }, index) in options"
            :key="index"
            class="flex w-auto pr-3 items-center space-x-2 pb-[0.8rem] cursor-pointer md:pr-5"
          >
            <span
              class="flex items-center cursor-pointer p-0.5 relative border border-brand-dark-blue"
            >
              <input
                type="checkbox"
                :id="field_name"
                v-model="form[field_name]"
                :value="value"
                autocomplete="off"
                class="peer h-3.5 w-3.5 cursor-pointer transition-all appearance-none bg-brand-dark-blue/10 checked:bg-slate-800 checked:border-slate-800"
              />
              <span
                class="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              />
            </span>
            <span v-if="options.length > 1">{{ value }}</span>
          </label>
        </CheckboxContainer>
        <CheckboxContainer
          v-if="type === 'radio'"
          :label="label"
          :class="width"
          :error-message="error_message"
          :errors="formObject.errors"
          :field="field_name"
        >
          <label
            v-for="({ value }, index) in options"
            :key="index"
            class="flex w-auto pr-3 items-center space-x-2 pb-[0.8rem] cursor-pointer md:pr-5"
          >
            <span
              class="flex items-center cursor-pointer p-0.5 relative border rounded-full border-brand-dark-blue"
            >
              <input
                type="radio"
                :id="field_name + '-' + index"
                v-model="formObject[field_name]"
                :value="value"
                autocomplete="off"
                class="peer h-4 w-4 cursor-pointer transition-all appearance-none rounded-full bg-brand-dark-blue/10 checked:bg-brand-dark-blue checked:border-brand-dark-blue"
              />
              <span
                class="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              />
            </span>
            <span>{{ value }}</span>
          </label>
        </CheckboxContainer>
      </template>
      <div class="col-span-2 py-4">
        <Button
          type="submit"
          :disabled="formObject.processing"
          class="px-0 ml-auto link"
          variant="link"
        >
          <span>Submit</span>
          <Loading v-if="formObject.processing" />
          <ButtonArrow v-else />
        </Button>
      </div>
      <div v-if="success || formHasErrors" class="col-span-2">
        <FeedbackMessage
          v-if="success"
          :message="
            props?.form?.success_message ?? 'Thank you for your submission?'
          "
          :error="false"
          class="pt-0"
        />
        <FeedbackMessage
          v-if="formHasErrors"
          message="The form has errors, please check the fields and try again."
          :error="true"
          class="pt-0"
        />
      </div>
    </form>
  </div>
</template>

<style scoped></style>
